// ============================================================================
// BID HOOKS
// React hooks for the Bid Response module: document upload/list/delete
// (story #174), plus requirement extraction/list/review (story #175). Upload
// flow mirrors useKnowledgeBase.ts's useUploadDocument: generate presigned
// URL → PUT direct to S3 → done. Extraction is a separate, explicit trigger
// (POST .../extract) rather than automatic on upload.
// ============================================================================

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import {
  bidDocumentsAPI,
  bidRequirementsAPI,
  bidExtractionsAPI,
  bidEvidenceAPI,
  BidDiffResult,
  BidDocument,
  BidDocumentClass,
  BidEvidence,
  BidEvidenceMetadata,
  BidEvidenceType,
  BidExtraction,
  BidRequirement,
  BidRequirementReviewStatus
} from '../services/bidService';

export const useBidDocuments = (
  projectId: string,
  docClass?: BidDocumentClass
): UseQueryResult<BidDocument[], Error> => {
  return useQuery({
    queryKey: ['bid-documents', projectId, docClass],
    queryFn: async () => (await bidDocumentsAPI.list(projectId, docClass)).data.documents,
    enabled: !!projectId
  });
};

export const useUploadBidDocument = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      docClass,
      evidence,
      supersedesDocumentId,
      onProgress
    }: {
      file: File;
      docClass: BidDocumentClass;
      evidence?: BidEvidenceMetadata;
      supersedesDocumentId?: string;
      onProgress?: (progress: number) => void;
    }) => {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      const effectiveType = file.type || (extension === 'md' ? 'text/markdown' : '');

      const { data: uploadData } = await bidDocumentsAPI.generateUploadUrl(
        projectId, file.name, effectiveType, file.size, docClass, evidence, supersedesDocumentId
      );

      await axios.put(uploadData.uploadUrl, file, {
        headers: { 'Content-Type': effectiveType || file.type },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress((progressEvent.loaded / progressEvent.total) * 100);
          }
        }
      });

      return uploadData.documentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-documents', projectId] });
    }
  });
};

export const useDeleteBidDocument = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => bidDocumentsAPI.delete(projectId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-documents', projectId] });
    }
  });
};

export const useTriggerExtraction = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => bidDocumentsAPI.triggerExtraction(projectId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-documents', projectId] });
      queryClient.invalidateQueries({ queryKey: ['bid-requirements', projectId] });
    }
  });
};

export const useBidRequirements = (
  projectId: string,
  documentId?: string
): UseQueryResult<BidRequirement[], Error> => {
  return useQuery({
    queryKey: ['bid-requirements', projectId, documentId],
    queryFn: async () => (await bidRequirementsAPI.list(projectId, documentId)).data.requirements,
    enabled: !!projectId
  });
};

// Story #182 — link/relink which document an addendum supersedes, independent of upload time.
export const useSetSupersedesDocumentId = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, supersedesDocumentId }: { documentId: string; supersedesDocumentId: string | null }) =>
      bidDocumentsAPI.setSupersedesDocumentId(projectId, documentId, supersedesDocumentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-documents', projectId] });
    }
  });
};

// Story #182 — run (or re-run) the addenda diff for a document against its declared predecessor.
export const useRunAddendumDiff = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => bidDocumentsAPI.runDiff(projectId, documentId),
    onSuccess: (_data, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['bid-diff', projectId, documentId] });
      queryClient.invalidateQueries({ queryKey: ['bid-requirements', projectId] });
    }
  });
};

export const useAddendumDiff = (
  projectId: string,
  documentId: string | undefined,
  enabled: boolean
): UseQueryResult<BidDiffResult, Error> => {
  return useQuery({
    queryKey: ['bid-diff', projectId, documentId],
    queryFn: async () => (await bidDocumentsAPI.getDiff(projectId, documentId as string)).data,
    enabled: !!projectId && !!documentId && enabled
  });
};

// ── Extractions + evidence (session hook — story #177) ────────────────────

export const useBidExtraction = (
  projectId: string,
  sessionId: string
): UseQueryResult<BidExtraction | null, Error> => {
  return useQuery({
    queryKey: ['bid-extraction', projectId, sessionId],
    queryFn: async () => {
      try {
        return (await bidExtractionsAPI.get(projectId, sessionId)).data.extraction;
      } catch (e: any) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!projectId && !!sessionId
  });
};

export const useTriggerBidExtraction = (projectId: string, sessionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionType: string) => bidExtractionsAPI.trigger(projectId, sessionId, sessionType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-extraction', projectId, sessionId] });
    }
  });
};

export const useConfirmBidExtraction = (projectId: string, sessionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => bidExtractionsAPI.confirm(projectId, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-extraction', projectId, sessionId] });
      queryClient.invalidateQueries({ queryKey: ['bid-requirements', projectId] });
    }
  });
};

export const useBidEvidence = (
  projectId: string,
  evidenceType?: BidEvidenceType
): UseQueryResult<BidEvidence[], Error> => {
  return useQuery({
    queryKey: ['bid-evidence', projectId, evidenceType],
    queryFn: async () => (await bidEvidenceAPI.list(projectId, evidenceType)).data.evidence,
    enabled: !!projectId
  });
};

export const usePullBidEvidence = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => bidEvidenceAPI.pull(projectId, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-evidence', projectId] });
    }
  });
};

export const useReviewRequirement = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requirementId,
      reviewStatus,
      openIssues
    }: {
      requirementId: string;
      reviewStatus?: BidRequirementReviewStatus;
      openIssues?: string[];
    }) => bidRequirementsAPI.review(projectId, requirementId, { reviewStatus, openIssues }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-requirements', projectId] });
    }
  });
};
